import { toast } from 'sonner';
import { paths } from '@/routes/paths';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/store/auth-store';
import { useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  persistActiveTenant,
  firstClientIdFromInvite,
  stashPendingInviteTenant,
} from '@/lib/active-tenant';
import {
  useInvitationsControllerVerify,
  useInvitationsControllerAccept,
} from '@/apis/backend/generated/invitations/invitations';

import { Box, Paper, Button, Container, Typography, CircularProgress } from '@mui/material';

export const Route = createFileRoute('/accept-invitation/$token')({
  component: AcceptInvitationPage,
});

function extractErrorMessage(err: any): string {
  return (
    err?.response?.data?.message ??
    err?.data?.message ??
    err?.message ??
    ''
  );
}

function AcceptInvitationPage() {
  const { t } = useTranslation('common');
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, initComplete, refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [acceptedForOtherEmail, setAcceptedForOtherEmail] = useState(false);

  const { data, isLoading, error: verifyError } = useInvitationsControllerVerify(token, {
    query: {
      enabled: !!token,
      retry: false,
    },
  });

  const acceptMutation = useInvitationsControllerAccept();
  const acceptStartedRef = useRef(false);

  useEffect(() => {
    if (!verifyError) return;
    const message = extractErrorMessage(verifyError).toLowerCase();
    // Token verifies as "already accepted" -- this is a duplicate link click.
    // If the user is signed in, send them to the dashboard quietly; otherwise
    // ask them to sign in.
    if (message.includes('already been accepted')) {
      if (initComplete && isAuthenticated) {
        toast.success(t('acceptInvitation.alreadyAcceptedToast', 'You already accepted this invitation.'));
        navigate({ to: paths.dashboard.root });
      } else if (initComplete) {
        setError(t('acceptInvitation.alreadyAcceptedError', 'This invitation has already been accepted. Please sign in to continue.'));
      }
      return;
    }
    if (message.includes('expired')) {
      setError(t('acceptInvitation.expiredError', 'This invitation has expired. Ask the organization owner to send a new one.'));
      return;
    }
    if (message.includes('revoked')) {
      setError(t('acceptInvitation.revokedError', 'This invitation has been revoked.'));
      return;
    }
    setError(t('acceptInvitation.invalidLinkError', 'Invalid or expired invitation link.'));
  }, [verifyError, initComplete, isAuthenticated, navigate]);

  useEffect(() => {
    if (!data?.id || !initComplete) return;
    if (acceptStartedRef.current) return;
    acceptStartedRef.current = true;

    // Already authenticated: accept in place
    if (isAuthenticated) {
      acceptMutation.mutate(
        { token },
        {
          onSuccess: async () => {
            toast.success(t('acceptInvitation.joinedToast', 'Joined {{organizationName}}', { organizationName: data.organizationName }));
            // Make the invited org/client the active tenant so the /dashboard
            // resolver forwards here instead of the user's last-used org.
            persistActiveTenant(data.organizationId, firstClientIdFromInvite(data.clientIds));
            await refresh();
            // Refetch invitations AND the org/client membership lists -- the cached
            // org list predates this accept, so without invalidation the resolver
            // wouldn't see the just-joined org and would fall back to the old default.
            queryClient.invalidateQueries({
              predicate: (query) => {
                const key = query.queryKey[0];
                return (
                  typeof key === 'string' &&
                  (key.includes('/invitations') || key.includes('/organizations'))
                );
              },
            });
            navigate({ to: paths.dashboard.root });
          },
          onError: (err: any) => {
            const message = extractErrorMessage(err).toLowerCase();
            // Race condition or duplicate visit: invitation was just accepted
            // (often by this same user in another tab). Treat as success.
            if (message.includes('already been accepted')) {
              toast.success(t('acceptInvitation.alreadyAcceptedToast', 'You already accepted this invitation.'));
              navigate({ to: paths.dashboard.root });
              return;
            }
            // Email mismatch
            if (message.includes('different email')) {
              setAcceptedForOtherEmail(true);
              return;
            }
            setError(t('acceptInvitation.acceptFailedError', 'Could not accept this invitation.'));
          },
        },
      );
      return;
    }

    // Not authenticated: stash token + invited tenant, route to sign-up. The tenant is
    // promoted to active after the invite is accepted post-auth (see auth-store).
    toast.success(t('acceptInvitation.verifiedCreateAccountToast', 'Invitation verified! Create an account to join the team.'));
    localStorage.setItem('pendingInvitationToken', token);
    stashPendingInviteTenant(data.organizationId, firstClientIdFromInvite(data.clientIds));
    navigate({ to: paths.auth.signUp });
  }, [data, token, navigate, isAuthenticated, initComplete, acceptMutation, refresh, queryClient]);

  if (isLoading || !initComplete || acceptMutation.isPending) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (acceptedForOtherEmail) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              {t('acceptInvitation.differentAccountTitle', 'Different account required')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('acceptInvitation.differentAccountBody', 'This invitation was sent to {{email}}. Sign out and sign in with that email to accept it.', { email: data?.email })}
            </Typography>
            <Button variant="contained" onClick={() => navigate({ to: paths.dashboard.root })}>
              {t('acceptInvitation.backToDashboard', 'Back to dashboard')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" color="error" gutterBottom>
              {t('acceptInvitation.invalidInvitationTitle', 'Invalid Invitation')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => navigate({ to: paths.auth.signIn })}>
              {t('acceptInvitation.goToSignIn', 'Go to Sign In')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  );
}
